import {
  DEFAULT_CLUSTER_CONNECTIONS,
  APP_ZERO_CLUSTER_CONNECTIONS,
  ORIGINAL_CLUSTER_CONNECTIONS,
  type ClusterConnections,
  getConnectionUrls,
} from 'config/index';
import { ClusterRedirect } from 'generated/graphql';
import { client } from 'modules/Apollo/client';
import { createApolloLink } from 'modules/Apollo/links';
import { useEffect, useMemo } from 'react';
import { useCurrentUser } from './useCurrentUser/legacy';
import { priorClient } from 'modules/Apollo/priorClient';
import analytics from 'modules/analytics';
import { globalContext } from 'modules/analytics/constants';

/**
 * dynamic re-configuration of configured external connections
 * Use `useConnectionUrls()` instead to get one-time urls
 */
export function useClusterRedirects() {
  const { data: currentUser } = useCurrentUser();

  const clusterRedirect = currentUser?.me.clusterRedirect;

  useEffect(() => {
    if (clusterRedirect != null) {
      setClusterConnections({
        clusterRedirect,
      });
    }
  }, [clusterRedirect]);
}

type SetClusterConnectionProps = {
  clusterRedirect: ClusterRedirect;
};

export function setClusterConnections({ clusterRedirect }: SetClusterConnectionProps) {
  const setTargets = (clusterConnections: ClusterConnections) => {
    client.setLink(
      createApolloLink({
        httpUri: clusterConnections.graphqlUrl,
        wsUri: clusterConnections.graphqlWs,
      })
    );
    priorClient.setLink(
      createApolloLink({
        httpUri: clusterConnections.graphqlUrl,
        wsUri: clusterConnections.graphqlWs,
      })
    );
  };

  switch (clusterRedirect) {
    case ClusterRedirect.AppZero: {
      setTargets(APP_ZERO_CLUSTER_CONNECTIONS);
      break;
    }
    case ClusterRedirect.Original: {
      setTargets(ORIGINAL_CLUSTER_CONNECTIONS);
      break;
    }
    default: {
      setTargets(DEFAULT_CLUSTER_CONNECTIONS);
      break;
    }
  }

  analytics.addContext(globalContext.workspace.clusterConnection, clusterRedirect);
}

/**
 * returns a set of urls dependent on CLUSTER_REDIRECT flag and current hostname
 */
export function useConnectionUrls() {
  const { data: currentUser } = useCurrentUser();

  const urls = useMemo(() => {
    if (currentUser == null) return DEFAULT_CLUSTER_CONNECTIONS;

    return getConnectionUrls(currentUser.me.clusterRedirect);
  }, [currentUser]);

  return urls;
}
